import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getCurrentUser } from './Utils';
import { isAuthGuardActive, UserRole } from '../constants/defaultValues';
import { isAuthenticated, getSession } from '../services/auth';

/**
 * React Router v6 compatible ProtectedRoute
 * Uses Cognito session check for authentication
 *
 * The resolved auth result is cached at module scope so navigating between
 * pages doesn't re-run getSession() and flash a full-screen spinner on every
 * route change (the guard previously keyed its effect on location.pathname).
 * The session itself is validated once; call clearAuthCache() on sign-out.
 */
let authCache = null; // { isAuthed, user } once resolved

export const clearAuthCache = () => { authCache = null; };

const ProtectedRoute = ({ element: Element, roles = undefined }) => {
    const [authState, setAuthState] = useState(() =>
        authCache
            ? { isLoading: false, isAuthed: authCache.isAuthed, user: authCache.user }
            : { isLoading: true, isAuthed: false, user: null }
    );
    const location = useLocation();

    useEffect(() => {
        if (!isAuthGuardActive) {
            setAuthState({ isLoading: false, isAuthed: true, user: null });
            return;
        }
        // Already resolved this session — no re-check, no spinner.
        if (authCache) {
            setAuthState({ isLoading: false, isAuthed: authCache.isAuthed, user: authCache.user });
            return;
        }

        let cancelled = false;
        const checkAuth = async () => {
            try {
                const session = await getSession();
                const resolved = session
                    ? { isAuthed: true, user: { id: session.userId, email: session.email, role: 0 } }
                    : { isAuthed: false, user: null };
                authCache = resolved;
                if (!cancelled) setAuthState({ isLoading: false, ...resolved });
            } catch (error) {
                console.error('Auth check error:', error);
                const resolved = { isAuthed: false, user: null };
                // Don't cache a transient error as "not authed" — let the next
                // navigation retry rather than trapping the user at login.
                if (!cancelled) setAuthState({ isLoading: false, ...resolved });
            }
        };

        checkAuth();
        return () => { cancelled = true; };
    // Run once on mount; the module cache handles subsequent navigations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Show loading state while checking auth
    if (authState.isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: '#f5f4f2'
            }}>
                <div style={{
                    width: '32px',
                    height: '32px',
                    // App accent (lilac), matching the lazy-load spinner in App.jsx —
                    // was a different-brand magenta (#922c88).
                    border: '3px solid rgba(159, 140, 199, 0.2)',
                    borderTopColor: '#9f8cc7',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                }} />
                <style>{`
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    if (!isAuthGuardActive) {
        return Element;
    }

    if (!authState.isAuthed) {
        // Redirect to login, preserving the intended destination
        return <Navigate to="/user/login" state={{ from: location }} replace />;
    }

    // Check role-based access if roles are specified
    if (roles && authState.user) {
        if (!roles.includes(authState.user.role)) {
            // A real in-app page (with the shell) that explains the denial —
            // was "/unauthorized", a route that didn't exist, so the operator
            // silently fell through to the login screen.
            return <Navigate to="/app/system/unauthorized" replace />;
        }
    }

    return Element;
};

export { ProtectedRoute, UserRole };
